import React from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { installErrorReporter } from "./lib/errors";
import { startUpdateCheck } from "./lib/updateCheck";
import { startRum, startApiEventLogger } from "./lib/rum";
import { startEventFlush } from "./lib/events";

// Self-hosted variable UI fonts (see index.html — no Google Fonts <link>).
// Importing here lets Vite bundle the WOFF2 files same-origin so first paint
// doesn't wait on a third-party DNS + TLS handshake. Fraunces uses the full
// axis bundle (wght + opsz) so `font-optical-sizing: auto` (index.css)
// scales serif thickness with size — Lagoon §5 depends on this.
import "@fontsource-variable/fraunces/full.css";
import "@fontsource-variable/hanken-grotesk/index.css";
import "./index.css";

installErrorReporter();
// Deploy-update prompt (spec 09): notices a new deploy on app resume and
// offers a tap-to-reload, so installed copies don't run stale code for days.
startUpdateCheck();
// API request logging runs in every environment so the /debug timeline
// shows local traffic too. Web Vitals collection stays production-only —
// dev's HMR reloads would drown any real signal.
startApiEventLogger();
if (import.meta.env.PROD) startRum();

// Read ?ref= from the landing URL BEFORE the router has a chance to
// strip it, so a stranger arriving via a shared trip / poster gets
// credited to the sharer if they sign up.
import("./lib/auth").then((m) => m.captureReferralFromUrl());
// Diagnostic event flush runs in every env (including dev) — the whole
// point is to see what happened when something looks wrong, and dev is
// where you look first. startRum's fetch monkey-patch depends on this
// being on to emit api.request events.
startEventFlush();

// Register the service worker for offline + instant repeat loads. Skipped
// in dev (Vite's HMR doesn't play well with SW caching) and on browsers
// without SW support.
if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {
      /* registration failures are non-fatal */
    });
  });
}

const el = document.getElementById("root");
if (!el) throw new Error("Missing #root element");

createRoot(el).render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>,
);
